// The main screen: Register / Take out / Move (PRD §9), ported from the prototype wizard.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ItemDto } from '../../shared/api';
import {
  NUMCATS,
  OUTWHY,
  POS,
  REPAIR,
  STORAGE_TYPES,
  TYPES,
  allowedCC,
  checkNo,
  defaultCC,
  defaultQcNeeded,
  typeLabel,
  type Loc,
  type Pos,
  type StorageType,
} from '../../shared/domain';
import { ApiError, get, post, refresh } from '../api';
import { CameraScan } from '../components/camera';
import { ActTable, Err, QcTag, ShelfDraw, Tag, errMsg, hm, useApp, type StartReq } from '../components/ui';
import type { ActivityDto } from '../../shared/api';

type Action = 'register' | 'take' | 'move';
type Step = 'action' | 'shelf' | 'shelf2' | 'spot' | 'item' | 'new' | 'check' | 'qc' | 'details' | 'confirm' | 'done';

interface NewItem {
  artName: string;
  artId: number | null;
  type: StorageType;
  stock: boolean;
  unit: string;
  min: number;
  consumable: boolean;
  productPart: boolean;
  cc: string;
  proj: string;
  yy: string;
  qty: number;
  serial: string;
  qc: { result: 'Pass' | 'Fail'; note: string } | null;
}

interface W {
  step: Step;
  action: Action | null;
  no: string | null;
  isNew: boolean;
  nw: NewItem | null;
  fromShelf: string | null;
  from: Loc | null;
  to: { shelf?: string; level?: number; pos?: Pos };
  why: string;
  project: string;
  takeAll: boolean;
  left: number;
  comment: string;
  err: string;
  forceRepair: boolean;
  override: boolean;
  repair: string;
  needApproval: boolean;
  filter: string;
  tab: 'back' | 'new';
  doneText: string;
  busy: boolean;
}

const fresh = (): W => ({
  step: 'action',
  action: null,
  no: null,
  isNew: false,
  nw: null,
  fromShelf: null,
  from: null,
  to: {},
  why: OUTWHY[0],
  project: '',
  takeAll: true,
  left: 0,
  comment: '',
  err: '',
  forceRepair: false,
  override: false,
  repair: REPAIR[0],
  needApproval: false,
  filter: '',
  tab: 'back',
  doneText: '',
  busy: false,
});

const ACTNAME: Record<Action, string> = { register: 'Register', take: 'Take out', move: 'Move' };

function stepsFor(a: Action | null): [string, string][] {
  if (a === 'take') return [['shelf', 'Scan shelf'], ['item', 'Item number'], ['details', 'Details'], ['confirm', 'Check and save']];
  if (a === 'move') return [['shelf', 'Scan current shelf'], ['item', 'Item number'], ['shelf2', 'Scan new shelf'], ['spot', 'Level and side'], ['details', 'Details'], ['confirm', 'Check and save']];
  return [['shelf', 'Scan shelf'], ['spot', 'Level and side'], ['item', 'Item number'], ['details', 'Details'], ['confirm', 'Check and save']];
}

const Icon = {
  in: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
      <path d="M3 7l9-4 9 4v10l-9 4-9-4z" />
      <path d="M12 8v7M9 12l3 3 3-3" />
    </svg>
  ),
  out: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
      <path d="M3 7l9-4 9 4v10l-9 4-9-4z" />
      <path d="M12 15V8M9 11l3-3 3 3" />
    </svg>
  ),
  move: (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
      <rect x="2" y="5" width="7" height="14" rx="1" />
      <rect x="15" y="5" width="7" height="14" rx="1" />
      <path d="M10 12h4M12 10l2 2-2 2" />
    </svg>
  ),
};

export function Scan({ pending, clearPending }: { pending: StartReq | null; clearPending: () => void }) {
  const { d, go, openItem } = useApp();
  const [w, setW] = useState<W>(fresh);
  const set = (p: Partial<W>) => setW((x) => ({ ...x, ...p }));
  const [cam, setCam] = useState<null | ((c: string) => void)>(null);
  const recent = useQuery({ queryKey: ['acts', 'recent'], queryFn: () => get<ActivityDto[]>('/api/activities?limit=5') });

  const curYY = d.today.slice(2, 4);
  const yearOpts = Array.from({ length: Math.max(1, +curYY - 25) }, (_, k) => String(26 + k));

  // ---------- current item / article ----------
  const curItem = (s: W = w): (ItemDto & { draft?: true }) | null => {
    if (!s.isNew) return s.no ? (d.item(s.no) ?? null) : null;
    return null;
  };
  const curArt = (s: W = w) => {
    if (!s.isNew) {
      const i = curItem(s);
      return i ? d.artOf(i) : null;
    }
    const n = s.nw!;
    const known = n.artId ? d.art(n.artId) : null;
    if (known) return known;
    return { id: 0, name: n.artName, type: n.type, stock: n.stock, unit: n.unit, min: n.min, consumable: n.consumable, productPart: n.productPart, qcNeeded: defaultQcNeeded(n.type, n.productPart), qcEvery: 12 } as ReturnType<typeof d.artOf>;
  };
  const curQcOk = (s: W = w): { ok: boolean; t?: string } => {
    const a = curArt(s)!;
    if (!s.isNew) {
      const q = d.qcOf(curItem(s)!);
      return { ok: q.ok, t: q.t };
    }
    if (!a.qcNeeded) return { ok: true };
    if (!s.nw!.qc) return { ok: false, t: 'QC missing' };
    return s.nw!.qc.result === 'Pass' ? { ok: true } : { ok: false, t: 'QC failed' };
  };

  // ---------- number preview ----------
  const nw = w.nw;
  const cat = nw ? NUMCATS[nw.cc] : undefined;
  const preview = useQuery({
    queryKey: ['nextNo', nw?.cc, cat?.long ? nw?.proj : '', nw?.yy],
    queryFn: () => get<{ no: string | null }>(`/api/numbers/next?cc=${nw!.cc}&proj=${cat?.long ? nw!.proj : ''}&yy=${nw!.yy}`),
    enabled: !!nw && !!cat && (!cat.long || !!nw.proj),
  });

  // ---------- flow ----------
  const reset = () => setW(fresh());
  const pickAction = (a: Action) => setW({ ...fresh(), action: a, step: 'shelf' });

  const runChecks = (s: W, qcPassed = false): W => {
    const sh = d.shelf(s.to.shelf)!;
    const a = curArt(s)!;
    if (s.forceRepair && !d.isRepair(sh)) return { ...s, err: 'This item is damaged or failed QC. It can only go to R1.', override: false, to: {}, step: s.action === 'move' ? 'shelf2' : 'shelf' };
    const home = d.homeOfArt(a);
    if (!d.isRepair(sh) && sh.code !== home && !s.override) return { ...s, step: 'check' };
    if (!d.isRepair(sh) && a.qcNeeded && !qcPassed && !curQcOk(s).ok) return { ...s, step: 'qc' };
    return { ...s, step: 'details' };
  };

  const scanShelf = (v: string) => {
    const c = v.trim().toUpperCase();
    const sh = d.shelf(c);
    if (!sh) return set({ err: checkNo(c).ok ? 'That looks like an item number. Scan the shelf QR code first.' : `No shelf with code ${c}.` });
    if (d.isLocked(sh) && !d.isOwner) return set({ err: 'Only the warehouse owner can use the locked storage L1.' });
    if (w.action === 'register') {
      if (w.forceRepair && !d.isRepair(sh)) return set({ err: 'This item is damaged or failed QC. It can only go to R1.' });
      return set({ err: '', to: { shelf: sh.code }, step: 'spot' });
    }
    set({ err: '', fromShelf: sh.code, step: 'item', filter: '' });
  };
  const scanShelf2 = (v: string, base: W = w) => {
    const c = v.trim().toUpperCase();
    const sh = d.shelf(c);
    if (!sh) return setW({ ...base, err: `No shelf with code ${c}.` });
    if (d.isLocked(sh) && !d.isOwner) return setW({ ...base, err: 'Only the warehouse owner can use the locked storage L1.' });
    if (base.forceRepair && !d.isRepair(sh)) return setW({ ...base, err: 'This item is damaged or failed QC. It can only go to R1.' });
    setW({ ...base, err: '', to: { shelf: sh.code }, step: 'spot' });
  };

  const spotNext = () => {
    const f = w.from;
    if (f && f.shelf === w.to.shelf && f.level === w.to.level && f.pos === w.to.pos) return set({ err: 'This is the same place it is now. Pick another place.' });
    if (w.action === 'register' && !w.no && !w.isNew) return set({ err: '', step: 'item', filter: '' });
    setW(runChecks({ ...w, err: '' }));
  };

  const pickItem = (v: string, base: W = w) => {
    const i = d.item(v);
    if (!i) {
      const c = checkNo(v);
      return setW({ ...base, err: c.ok ? `Number ${v.toUpperCase()} is not in the app. For a new item, use "New item, needs a number".` : `${c.err}.` });
    }
    if (base.action === 'register') {
      if (i.status === 'in') return setW({ ...base, err: `${i.no} is already on ${d.addr(i.loc)}. To change its place, use Move.` });
      if (d.artOf(i).type === '2-1' && !d.isOwner) return setW({ ...base, err: 'Products (2-1) go in the locked storage L1. Only the warehouse owner can put them in.' });
      const s = { ...base, err: '', no: i.no, isNew: false, left: i.qty };
      return setW(s.to.shelf && s.to.level ? runChecks(s) : { ...s, step: 'shelf' });
    }
    if (i.status !== 'in') return setW({ ...base, err: `${i.no} is not on a shelf. ${i.status === 'gone' ? `It is marked ${i.gone?.reason.toLowerCase()}.` : `It was taken out by ${i.out?.by ?? 'someone'}.`}` });
    if (i.loc!.shelf !== base.fromShelf) return setW({ ...base, err: `${i.no} is on ${d.addr(i.loc)}, not on ${base.fromShelf}. Scan that shelf, or check the label.` });
    if ((d.artOf(i).type === '2-1' || d.isLocked(d.shelf(i.loc!.shelf))) && !d.isOwner) return setW({ ...base, err: 'This is a product in the locked storage L1. Only the warehouse owner can take it out or move it.' });
    const uf = d.usedFor(i);
    const s: W = {
      ...base,
      err: '',
      no: i.no,
      from: { ...i.loc! },
      why: d.artOf(i).type === '1-1' ? 'Using in the workshop' : OUTWHY[0],
      project: uf && uf !== '0301' ? uf : '',
      left: i.qty,
      takeAll: true,
      step: base.action === 'take' ? 'details' : 'shelf2',
    };
    return setW(s);
  };

  // Start from an item card or "Fixed, send back".
  useEffect(() => {
    if (!pending) return;
    clearPending();
    const i = pending.no ? d.item(pending.no) : undefined;
    const base: W = { ...fresh(), action: pending.action, comment: pending.comment ?? '' };
    if (pending.action === 'register') return setW({ ...base, step: 'shelf', no: i?.no ?? null, left: i?.qty ?? 0 });
    if (!i || !i.loc) return setW({ ...base, step: 'shelf' });
    const s0: W = { ...base, fromShelf: i.loc.shelf, step: 'item' };
    if (pending.toShelf) {
      // pickItem then scan the target shelf, in one go
      const uf = d.usedFor(i);
      const s1: W = { ...s0, no: i.no, from: { ...i.loc }, project: uf && uf !== '0301' ? uf : '', left: i.qty, step: 'shelf2' };
      return scanShelf2(pending.toShelf, s1);
    }
    pickItem(i.no, s0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const startNew = () => {
    const n: NewItem = { artName: '', artId: null, type: '2-2', stock: false, unit: 'pc', min: 0, consumable: false, productPart: false, cc: '03', proj: '0301', yy: curYY, qty: 1, serial: '', qc: null };
    set({ tab: 'new', isNew: true, no: null, err: '', nw: n, step: 'new' });
  };
  const fixCC = (n: NewItem): NewItem => {
    if (allowedCC(n).includes(n.cc)) return n;
    const cc = defaultCC(n);
    return { ...n, cc, proj: cc === '03' ? '0301' : (d.b.projects.find((p) => p.code.startsWith('02'))?.code ?? '') };
  };
  const setNw = (p: Partial<NewItem>) => setW((x) => ({ ...x, nw: fixCC({ ...x.nw!, ...p }) }));
  const setArt = (v: string) => {
    const a = d.artByName(v);
    const n = { ...w.nw!, artName: v.trim(), artId: a?.id ?? null };
    if (a) Object.assign(n, { type: a.type, stock: a.stock, unit: a.unit, consumable: a.consumable, productPart: a.productPart });
    set({ nw: fixCC(n) });
  };
  const newNext = () => {
    const n = w.nw!;
    if (!n.artName) return set({ err: 'Pick or type the article name.' });
    if (!allowedCC(n).includes(n.cc)) return set({ err: 'This number category does not fit the article.' });
    if (NUMCATS[n.cc]!.long && !n.proj) return set({ err: 'Choose the project. Add R&D codes on the Projects page first.' });
    if (n.type === '2-1' && !d.isOwner) return set({ err: 'Only the warehouse owner can register products (2-1).' });
    if (preview.data && !preview.data.no) return set({ err: 'This group already has 999 numbers. Ask the warehouse owner.' });
    setW(runChecks({ ...w, err: '' }));
  };

  const rescanTarget = (s: W): W => ({ ...s, override: false, to: {}, step: s.action === 'move' ? 'shelf2' : 'shelf' });

  const gateQC = async (result: 'Pass' | 'Fail', note: string) => {
    note = note.trim();
    if (result === 'Fail' && !note) return set({ err: 'Write what is wrong before you save a failed QC.' });
    if (w.isNew) {
      const s = { ...w, err: '', nw: { ...w.nw!, qc: { result, note } } };
      return setW(result === 'Pass' ? runChecks(s, true) : rescanTarget({ ...s, forceRepair: true, comment: 'QC failed. ' + note }));
    }
    set({ busy: true });
    try {
      await post(`/api/items/${encodeURIComponent(w.no!)}/qc`, { result, note });
      await refresh();
      const s = { ...w, err: '', busy: false };
      setW(result === 'Pass' ? runChecks(s, true) : rescanTarget({ ...s, forceRepair: true, comment: 'QC failed. ' + note }));
    } catch (e) {
      set({ busy: false, err: errMsg(e) });
    }
  };

  const damaged = () => setW(rescanTarget({ ...w, forceRepair: true, comment: w.comment || 'Damaged.' }));

  const detailsNext = () => {
    const i = curItem();
    const a = curArt()!;
    if (w.action === 'take') {
      if (i && d.reserved(i) && !w.project) return set({ err: 'This item is reserved. Choose the project.' });
      if (a.stock && !w.takeAll && !(w.left >= 0 && i && w.left < i.qty)) return set({ err: `How much is left must be between 0 and ${i ? d.fmtQty(i) : ''}.` });
      return set({ err: '', step: 'confirm', needApproval: !!i && d.reserved(i) && w.project !== d.usedFor(i) && !d.isOwner });
    }
    if (w.action === 'register' && !w.isNew && a.stock && !(w.left >= 0)) return set({ err: 'Enter how much is in the box.' });
    set({ err: '', step: 'confirm' });
  };

  const buildDesc = () => {
    const i = curItem();
    const a = curArt()!;
    const c = w.comment ? ' ' + w.comment.trim().replace(/\.?$/, '.') : '';
    if (w.action === 'take' && i) {
      const p = w.project ? ` (${d.projTxt(w.project)})` : '';
      if (a.stock && !w.takeAll) return `Took ${+(i.qty - w.left).toFixed(2)} ${a.unit} ${a.name} from the box on ${d.addr(w.from)} for ${w.why.toLowerCase()}${p}. ${+w.left} ${a.unit} left in the box.${c}`;
      return `Taken out from ${d.addr(w.from)} for ${w.why.toLowerCase()}${p}.${c}`;
    }
    const to = { shelf: w.to.shelf!, level: w.to.level!, pos: w.to.pos! };
    const r1 = d.isRepair(d.shelf(w.to.shelf)) ? ` Status: ${w.repair.toLowerCase()}.` : '';
    if (w.action === 'move') return `Moved from ${d.addr(w.from)} to ${d.addr(to)}.${r1}${c}`;
    if (w.isNew) return `New item registered on ${d.addr(to)}. Article: ${a.name}.${a.stock ? ` Quantity: ${w.nw!.qty} ${a.unit}.` : ''}${r1}${c}`;
    return `Put back on ${d.addr(to)}.${a.stock ? ` ${w.left} ${a.unit} in the box.` : ''}${r1}${c}`;
  };

  const commit = async () => {
    set({ busy: true, err: '' });
    try {
      let r: { no: string; text: string; needApproval?: boolean };
      if (w.action === 'take') {
        r = await post('/api/actions/take', { no: w.no, fromShelf: w.fromShelf, why: w.why, project: w.project, takeAll: w.takeAll, left: w.left, comment: w.comment });
      } else {
        const place = { shelf: w.to.shelf, level: w.to.level, pos: w.to.pos, override: w.override, forceRepair: w.forceRepair, repair: w.repair, comment: w.comment };
        if (w.action === 'move') r = await post('/api/actions/move', { no: w.no, ...place });
        else if (w.isNew) {
          const n = w.nw!;
          r = await post('/api/actions/register', {
            ...place,
            newItem: {
              ...(n.artId ? { articleId: n.artId } : { article: { name: n.artName, type: n.type, stock: n.stock, unit: n.unit, min: n.min, consumable: n.consumable, productPart: n.productPart } }),
              cc: n.cc,
              proj: NUMCATS[n.cc]!.long ? n.proj : '',
              yy: n.yy,
              qty: n.qty,
              serial: n.serial,
              ...(n.qc ? { qc: n.qc } : {}),
            },
          });
        } else r = await post('/api/actions/register', { ...place, existing: { no: w.no, ...(curArt()!.stock ? { qty: w.left } : {}) } });
      }
      await refresh();
      setW((x) => ({ ...x, busy: false, step: 'done', no: r.no, doneText: r.text, needApproval: !!r.needApproval }));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) return setW((x) => ({ ...x, busy: false, step: 'check' }));
      set({ busy: false, err: errMsg(e) });
    }
  };

  const openCam = useCallback((fn: (c: string) => void) => setCam(() => fn), []);
  const onCode = useCallback(
    (c: string) => {
      const fn = cam;
      setCam(null);
      fn?.(c);
    },
    [cam],
  );

  // ---------- views ----------
  const ItemLine = () => {
    const i = curItem();
    const a = curArt();
    if (!a) return null;
    return (
      <>
        <div className="row">
          <span className="code" style={{ fontSize: 20 }}>
            {i?.no ?? 'New number'}
          </span>
          <span>{a.name}</span>
          {i && <QcTag i={i} />}
          {a.stock && i && <Tag cls="soft">{d.fmtQty(i)}</Tag>}
        </div>
        <p className="meta" style={{ margin: '4px 0 0' }}>
          {typeLabel(a)}
          {i?.proj ? ` · bought for ${d.projTxt(i.proj)}` : ''}
        </p>
      </>
    );
  };

  const Place = ({ l }: { l: Loc | null }) => (
    <>
      <span className="code">{d.addr(l)}</span> <span className="meta">{d.addrLong(l)}</span>
    </>
  );
  const toLoc = (): Loc | null => (w.to.shelf && w.to.level && w.to.pos ? { shelf: w.to.shelf, level: w.to.level, pos: w.to.pos } : null);

  if (w.step === 'action')
    return (
      <>
        <div className="homehead">
          <div>
            <h1>What do you want to do?</h1>
            <p className="sub">Pick one action. Then scan the shelf QR code and type or pick the item number from its label.</p>
          </div>
          <img src="/logo.webp" alt="Boghche" className="homelogo" />
        </div>
        <div className="acts">
          <button className="act" onClick={() => pickAction('register')}>
            {Icon.in}
            <b>Register</b>Put an item on a shelf: a new item, or an item that comes back.
          </button>
          <button className="act" onClick={() => pickAction('take')}>
            {Icon.out}
            <b>Take out</b>Take an item, or part of a box, from a shelf.
          </button>
          <button className="act" onClick={() => pickAction('move')}>
            {Icon.move}
            <b>Move</b>Move an item from one place to another, for example to R1.
          </button>
        </div>
        <p className="meta" style={{ marginTop: 14 }}>
          Used or broke consumables today? Use the{' '}
          <button className="linkbtn" onClick={() => go('/report')}>
            daily report
          </button>
          .
        </p>
        <h2>Last activities</h2>
        <ActTable list={recent.data ?? []} compact />
      </>
    );

  if (w.step === 'done') {
    const kit = !!w.no && w.no === d.b.suitcaseNo && w.action !== 'move' && !w.needApproval;
    return (
      <>
        <h1>{w.needApproval ? 'Sent for approval' : 'Saved'}</h1>
        <div className="panel">
          {w.isNew && (
            <div className="numbox big">
              <span className="meta">Write this number on the label and stick it on the item</span>
              <b className="code">{w.no}</b>
            </div>
          )}
          <p style={{ margin: '0 0 8px' }}>{w.doneText}</p>
          <p className="meta" style={{ margin: 0 }}>
            Saved by {d.me.name} at {hm(new Date().toISOString())}. You can find it in the activity log.
          </p>
          <div className="row" style={{ marginTop: 14 }}>
            {kit && (
              <button className="btn primary" onClick={() => go(`/kit?check=${w.action === 'take' ? 'before' : 'return'}`)}>
                {w.action === 'take' ? 'Check suitcase before installation' : 'Check suitcase contents now'}
              </button>
            )}
            <button className={`btn ${kit ? '' : 'primary'}`} onClick={reset}>
              Next action
            </button>
            {!w.needApproval && w.no && (
              <button className="btn" onClick={() => openItem(w.no!)}>
                Open item card
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  let body: ReactNode = null;
  const sh = d.shelf(w.to.shelf);

  if (w.step === 'shelf')
    body = (
      <>
        <p className="q">{w.action === 'register' ? 'Scan the QR code of the shelf where you put the item' : 'Scan the QR code of the shelf where the item is now'}</p>
        {w.action === 'register' && w.no && (
          <>
            <ItemLine />
            <br />
          </>
        )}
        {w.forceRepair && (
          <p className="err" style={{ marginTop: 0 }}>
            This item is damaged or failed QC. Scan R1, the repair / unused corner.
          </p>
        )}
        <ScanBox fn={scanShelf} ph="Shelf code, e.g. S1" onCam={openCam} />
      </>
    );

  if (w.step === 'shelf2') {
    const i = curItem()!;
    body = (
      <>
        <ItemLine />
        <p style={{ margin: '12px 0 0' }}>
          From: <Place l={w.from} />
        </p>
        <p className="q" style={{ marginTop: 16 }}>
          Scan the QR code of the new shelf
        </p>
        {w.forceRepair ? (
          <p className="err" style={{ marginTop: 0 }}>
            This item is damaged or failed QC. Scan R1.
          </p>
        ) : (
          <p className="meta" style={{ margin: '-4px 0 0' }}>
            Home area for this item: <b>{d.homeOf(i)}</b> ({d.shelf(d.homeOf(i))?.name}).
          </p>
        )}
        <ScanBox fn={(v) => scanShelf2(v)} ph="Shelf code, e.g. R1" onCam={openCam} />
      </>
    );
  }

  if (w.step === 'spot' && sh)
    body = (
      <>
        {(w.no || w.isNew) && (
          <>
            <ItemLine />
            <br />
          </>
        )}
        <p style={{ margin: 0 }}>
          Shelf: <span className="code">{sh.code}</span> {sh.name}
        </p>
        <div className="row" style={{ alignItems: 'flex-start', gap: 28, marginTop: 16 }}>
          <div style={{ flex: 1, minWidth: 250 }}>
            <p className="q">Which level?</p>
            <p className="meta" style={{ margin: '-4px 0 8px' }}>
              Levels are counted from the bottom. Level 1 is the bottom.
            </p>
            <div className="seg">
              {Array.from({ length: sh.levels }, (_, k) => k + 1).map((l) => (
                <button key={l} className={`btn ${w.to.level === l ? 'on' : ''}`} onClick={() => set({ to: { ...w.to, level: l } })}>
                  Level {l}
                  {l === 1 ? ' (bottom)' : l === sh.levels ? ' (top)' : ''}
                </button>
              ))}
            </div>
            <p className="q">Which part of the level?</p>
            <div className="seg">
              {(Object.entries(POS) as [Pos, string][]).map(([k, v]) => (
                <button key={k} className={`btn ${w.to.pos === k ? 'on' : ''}`} onClick={() => set({ to: { ...w.to, pos: k } })}>
                  {v}
                </button>
              ))}
            </div>
            <button className="btn primary" disabled={!w.to.level || !w.to.pos} onClick={spotNext}>
              Next
            </button>
          </div>
          <div style={{ flex: 1, minWidth: 250 }}>
            <p className="meta" style={{ margin: '0 0 6px' }}>
              Or tap the place on the shelf:
            </p>
            <ShelfDraw sh={sh} pick={w.to} onPick={(level, pos) => set({ to: { ...w.to, level, pos } })} />
          </div>
        </div>
      </>
    );

  if (w.step === 'item') {
    const f = w.filter.toLowerCase();
    const match = (i: ItemDto) => !f || `${i.no} ${d.artOf(i).name}`.toLowerCase().includes(f);
    const pickList = (list: ItemDto[]) =>
      list.length ? (
        <div className="picklist">
          {list.slice(0, 200).map((i) => (
            <button key={i.no} onClick={() => pickItem(i.no)}>
              <span className="code">{i.no}</span>
              <span>
                {d.artOf(i).name}
                {d.artOf(i).stock ? ' · ' + d.fmtQty(i) : ''}
              </span>
              <span className="meta">{i.status === 'in' ? d.addr(i.loc) : i.status === 'gone' ? i.gone?.reason : 'taken out by ' + (i.out?.by ?? '')}</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="meta">No items match.</p>
      );
    const typeBox = (
      <label className="field" style={{ marginTop: 10 }}>
        Type the number from the label, or search by article name
        <input
          autoFocus
          list="nolist"
          value={w.filter}
          placeholder="e.g. PH-26-03-0322-003 or Syslogic"
          onChange={(e) => set({ filter: e.target.value })}
          onKeyDown={(e) => e.key === 'Enter' && d.item(w.filter) && pickItem(w.filter)}
          autoComplete="off"
        />
        <datalist id="nolist">
          {d.b.items.map((i) => (
            <option key={i.no} value={i.no}>
              {d.artOf(i).name}
            </option>
          ))}
        </datalist>
      </label>
    );
    if (w.action === 'register') {
      const back = d.b.items.filter((i) => i.status !== 'in' && match(i));
      body = (
        <>
          <p style={{ margin: '0 0 12px' }}>
            Place: <Place l={toLoc()} />
          </p>
          <div className="row" style={{ marginBottom: 10 }}>
            <button className={`btn ${w.tab === 'back' ? 'on' : ''}`} onClick={() => set({ tab: 'back' })}>
              Item with a number (coming back)
            </button>
            <button className={`btn ${w.tab === 'new' ? 'on' : ''}`} onClick={startNew}>
              New item, needs a number
            </button>
          </div>
          {typeBox}
          <p className="meta" style={{ margin: '10px 0 6px' }}>
            Items not on a shelf now:
          </p>
          {pickList(back)}
        </>
      );
    } else {
      const here = d.b.items.filter((i) => i.status === 'in' && i.loc?.shelf === w.fromShelf && match(i));
      body = (
        <>
          <p style={{ margin: '0 0 4px' }}>
            Shelf: <span className="code">{w.fromShelf}</span> {d.shelf(w.fromShelf)?.name}
          </p>
          {typeBox}
          <p className="meta" style={{ margin: '10px 0 6px' }}>
            Items on {w.fromShelf}:
          </p>
          {pickList(here)}
        </>
      );
    }
  }

  if (w.step === 'new' && nw) {
    const known = nw.artId ? d.art(nw.artId) : null;
    const ccOpts = Object.entries(NUMCATS).filter(([k]) => allowedCC(nw).includes(k));
    const projOpts = d.b.projects.filter((p) => p.code.slice(0, 2) === nw.cc);
    const stock = known ? known.stock : nw.stock;
    body = (
      <>
        <p style={{ margin: '0 0 12px' }}>
          Place: <Place l={toLoc()} />
        </p>
        <p className="q">1. Which article is it?</p>
        <p className="meta" style={{ margin: '-4px 0 8px' }}>
          Pick it from the list. If it is not there, type the name once. Next time it is in the list.
        </p>
        <input list="artlist" defaultValue={nw.artName} placeholder="e.g. Syslogic edge processor v2" style={{ width: '100%', maxWidth: 520 }} onChange={(e) => setArt(e.target.value)} />
        <datalist id="artlist">
          {d.b.articles.map((a) => (
            <option key={a.id} value={a.name} />
          ))}
        </datalist>
        {nw.artName &&
          (known ? (
            <p className="meta" style={{ margin: '8px 0 0' }}>
              Known article: {typeLabel(known)}
              {known.stock ? `, counted per box in ${known.unit}` : ', one number per piece'}.
            </p>
          ) : (
            <div className="box">
              <b>New article</b>
              <div className="row" style={{ alignItems: 'flex-end', marginTop: 8 }}>
                <label className="field">
                  Storage type
                  <select value={nw.type} onChange={(e) => setNw({ type: e.target.value as StorageType, ...(['2-2', '2-3'].includes(e.target.value) ? {} : { stock: false, consumable: false, productPart: false }) })}>
                    {STORAGE_TYPES.map((k) => (
                      <option key={k} value={k}>
                        {k} {TYPES[k].name} ({TYPES[k].home})
                      </option>
                    ))}
                  </select>
                </label>
                {['2-2', '2-3'].includes(nw.type) && (
                  <label className="field">
                    How is it counted?
                    <select value={nw.stock ? 'box' : 'piece'} onChange={(e) => setNw({ stock: e.target.value === 'box' })}>
                      <option value="piece">One number per piece</option>
                      <option value="box">One number per box, with quantity</option>
                    </select>
                  </label>
                )}
                {nw.stock && (
                  <>
                    <label className="field" style={{ width: 90 }}>
                      Unit
                      <input value={nw.unit} onChange={(e) => setNw({ unit: e.target.value || 'pc' })} />
                    </label>
                    {d.isOwner ? (
                      <label className="field" style={{ width: 110 }}>
                        Minimum
                        <input type="number" min={0} step="any" value={nw.min} onChange={(e) => setNw({ min: parseFloat(e.target.value) || 0 })} />
                      </label>
                    ) : (
                      <span className="meta">The warehouse owner sets the minimum.</span>
                    )}
                  </>
                )}
              </div>
              {nw.type === '2-2' && (
                <label className="row" style={{ marginTop: 10, gap: 8 }}>
                  <input type="checkbox" checked={nw.productPart} onChange={(e) => setNw({ productPart: e.target.checked })} /> Part of our product (needs QC)
                </label>
              )}
              {['2-2', '2-3'].includes(nw.type) && (
                <label className="row" style={{ marginTop: 6, gap: 8 }}>
                  <input type="checkbox" checked={nw.consumable} onChange={(e) => setNw({ consumable: e.target.checked, ...(e.target.checked ? { stock: true } : {}) })} /> Consumable (screws, bolts, blades, drill bits)
                </label>
              )}
            </div>
          ))}
        <p className="q" style={{ marginTop: 18 }}>
          2. Number group
        </p>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <label className="field">
            Category
            <select value={nw.cc} onChange={(e) => setNw({ cc: e.target.value, proj: d.b.projects.find((p) => p.code.slice(0, 2) === e.target.value)?.code ?? '' })}>
              {ccOpts.map(([k, v]) => (
                <option key={k} value={k}>
                  {k} {v.name}
                </option>
              ))}
            </select>
          </label>
          {NUMCATS[nw.cc]?.long && (
            <label className="field" style={{ minWidth: 240 }}>
              Project (bought for)
              <select value={nw.proj} onChange={(e) => setNw({ proj: e.target.value })}>
                {!projOpts.length && <option value="">No codes yet</option>}
                {projOpts.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.code} {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            Purchase year
            <select value={nw.yy} onChange={(e) => setNw({ yy: e.target.value })}>
              {yearOpts.map((y) => (
                <option key={y} value={y}>
                  20{y}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="meta" style={{ margin: '6px 0 0' }}>
          {nw.cc === '07' ? 'Tools always use 07.' : nw.cc === '06' ? 'Consumables always use 06.' : 'R&D and project items are reserved for their project.'}
        </p>
        <div className="row" style={{ alignItems: 'flex-end', marginTop: 14 }}>
          {stock ? (
            <label className="field" style={{ width: 150 }}>
              Quantity in the box ({known ? known.unit : nw.unit})
              <input type="number" min={0} step="any" value={nw.qty} onChange={(e) => setNw({ qty: parseFloat(e.target.value) || 0 })} />
            </label>
          ) : (
            <label className="field" style={{ minWidth: 220 }}>
              Serial number (optional)
              <input value={nw.serial} onChange={(e) => setNw({ serial: e.target.value })} placeholder="from the manufacturer" />
            </label>
          )}
        </div>
        <div className="numbox">
          <span className="meta">The app gives this number</span>
          <b className="code">{preview.data?.no ?? '—'}</b>
          <span className="meta">Users cannot choose numbers. The number is fixed when you save. Write it on the label after saving.</span>
        </div>
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn primary" onClick={newNext}>
            Next
          </button>
        </div>
      </>
    );
  }

  if (w.step === 'check' && sh) {
    const home = d.homeOfArt(curArt()!);
    body = (
      <>
        <ItemLine />
        <div className="box" style={{ borderColor: 'var(--warn)' }}>
          <p style={{ margin: 0 }}>
            <b>{sh.code}</b> is for {sh.what.toLowerCase()}. This item belongs in <b>{home}</b> ({d.shelf(home)?.name}).
          </p>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" onClick={() => setW(runChecks({ ...w, override: true }))}>
              Put it in {sh.code} anyway
            </button>
            <button className="btn" onClick={() => setW(rescanTarget(w))}>
              Scan another shelf
            </button>
          </div>
        </div>
      </>
    );
  }

  if (w.step === 'qc') body = <QcGate t={curQcOk().t ?? 'QC missing'} busy={w.busy} onSave={gateQC} line={<ItemLine />} />;

  if (w.step === 'details') {
    const i = curItem();
    const a = curArt()!;
    if (w.action === 'take' && i) {
      const res = d.reserved(i);
      body = (
        <>
          <ItemLine />
          <p style={{ margin: '10px 0 0' }}>
            From: <Place l={w.from} />
          </p>
          {res && (
            <p className="meta" style={{ margin: '6px 0 0' }}>
              Reserved: bought for <b>{d.projTxt(i.proj)}</b>, now used for <b>{d.projTxt(d.usedFor(i))}</b>. Another project needs the warehouse owner's approval.
            </p>
          )}
          <div className="row" style={{ marginTop: 16, alignItems: 'flex-end' }}>
            <label className="field" style={{ minWidth: 200 }}>
              Why is it taken out?
              <select value={w.why} onChange={(e) => set({ why: e.target.value })}>
                {OUTWHY.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="field" style={{ minWidth: 220 }}>
              For which project?{res ? '' : ' (optional)'}
              <select value={w.project} onChange={(e) => set({ project: e.target.value })}>
                {(!res || !w.project) && <option value="">{res ? 'Choose the project' : 'No project'}</option>}
                {d.b.projects.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.code} {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {a.stock && (
            <div className="box">
              <p className="q" style={{ margin: '0 0 8px' }}>
                This is a box with {d.fmtQty(i)}.
              </p>
              <div className="seg">
                <button className={`btn ${w.takeAll ? 'on' : ''}`} onClick={() => set({ takeAll: true })}>
                  I take the whole box
                </button>
                <button className={`btn ${!w.takeAll ? 'on' : ''}`} onClick={() => set({ takeAll: false })}>
                  I take some, the box stays
                </button>
              </div>
              {!w.takeAll && (
                <label className="field" style={{ maxWidth: 240 }}>
                  How much is left in the box now? ({a.unit})
                  <input type="number" min={0} step="any" value={w.left} onChange={(e) => set({ left: parseFloat(e.target.value) })} />
                </label>
              )}
            </div>
          )}
        </>
      );
    } else {
      body = (
        <>
          <ItemLine />
          <p style={{ margin: '10px 0 0' }}>
            {w.from ? (
              <>
                From <span className="code">{d.addr(w.from)}</span> to{' '}
              </>
            ) : (
              'To '
            )}
            <Place l={toLoc()} />
          </p>
          {d.isRepair(sh) && (
            <label className="field" style={{ marginTop: 14, maxWidth: 260 }}>
              Status in R1
              <select value={w.repair} onChange={(e) => set({ repair: e.target.value })}>
                {REPAIR.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
          )}
          {w.action === 'register' && !w.isNew && a.stock && (
            <label className="field" style={{ marginTop: 14, maxWidth: 240 }}>
              How much is in the box now? ({a.unit})
              <input type="number" min={0} step="any" value={w.left} onChange={(e) => set({ left: parseFloat(e.target.value) })} />
            </label>
          )}
        </>
      );
    }
    const toR1 = w.action !== 'take' && d.isRepair(sh);
    body = (
      <>
        {body}
        <label className="field" style={{ marginTop: 14 }}>
          {w.action === 'take' ? 'Description: what happens to the item?' : toR1 ? 'What is wrong, or why is it kept here?' : 'Comment (optional)'}
          <textarea value={w.comment} onChange={(e) => set({ comment: e.target.value })} placeholder={toR1 ? 'e.g. Connector loose' : w.action === 'take' ? 'e.g. For the second train in Istanbul' : 'e.g. Back from site, all OK'} />
        </label>
        {w.action !== 'take' && !toR1 && (
          <p style={{ margin: '12px 0 0' }}>
            <button className="btn small" onClick={damaged}>
              The item is damaged
            </button>{' '}
            <span className="meta">Damaged items go to R1.</span>
          </p>
        )}
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn primary" onClick={detailsNext}>
            Next: check
          </button>
        </div>
      </>
    );
  }

  if (w.step === 'confirm') {
    const i = curItem();
    const a = curArt()!;
    const qcOk = i ? null : curQcOk();
    body = (
      <>
        <div className="panel">
          <dl className="sum">
            <dt>Action</dt>
            <dd>
              <b>{ACTNAME[w.action!]}</b>
              {w.needApproval && (
                <>
                  {' '}
                  <Tag cls="warn">needs the warehouse owner's approval</Tag>
                </>
              )}
            </dd>
            <dt>Item number</dt>
            <dd>
              <span className="code">{w.no ?? preview.data?.no ?? '—'}</span>
              {w.isNew && (
                <>
                  {' '}
                  <Tag cls="ok">new number</Tag>
                </>
              )}
            </dd>
            <dt>Article</dt>
            <dd>
              {a.name}
              <br />
              <span className="meta">{typeLabel(a)}</span>
            </dd>
            {i?.proj && (
              <>
                <dt>Bought for</dt>
                <dd>{d.projTxt(i.proj)}</dd>
              </>
            )}
            <dt>QC</dt>
            <dd>{i ? <QcTag i={i} /> : qcOk?.ok ? <Tag cls="ok">OK</Tag> : <Tag cls="bad">Not done</Tag>}</dd>
            <dt>From</dt>
            <dd>{w.from ? <Place l={w.from} /> : w.isNew ? 'New item' : 'Not on a shelf'}</dd>
            <dt>To</dt>
            <dd>{w.action === 'take' ? (a.stock && !w.takeAll ? 'Stays on the shelf' : 'Out of the warehouse') : <Place l={toLoc()} />}</dd>
            <dt>Description</dt>
            <dd>{buildDesc()}</dd>
            <dt>User</dt>
            <dd>{d.me.name}</dd>
            <dt>Date and time</dt>
            <dd>Saved automatically</dd>
          </dl>
        </div>
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn primary" disabled={w.busy} onClick={() => void commit()}>
            {w.busy ? 'Saving…' : w.needApproval ? 'Send for approval' : `Save ${ACTNAME[w.action!].toLowerCase()}`}
          </button>
          <button className="btn" onClick={() => set({ step: 'details', err: '' })}>
            Change details
          </button>
        </div>
      </>
    );
  }

  const st = stepsFor(w.action);
  const cur = ({ new: 'item', check: 'details', qc: 'details' } as Record<string, string>)[w.step] ?? w.step;
  const idx = st.findIndex((s) => s[0] === cur);
  return (
    <>
      <h1>{ACTNAME[w.action!]}</h1>
      <div className="steps">
        {st.map((s, k) => (
          <span key={s[0]} className={k < idx ? 'done' : k === idx ? 'now' : ''}>
            {k + 1}. {s[1]}
          </span>
        ))}
      </div>
      <div className="panel">
        {body}
        <Err e={w.err} />
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn" onClick={reset}>
          Cancel
        </button>
      </div>
      {cam && <CameraScan onCode={onCode} onClose={() => setCam(null)} />}
    </>
  );
}

function QcGate({ t, busy, onSave, line }: { t: string; busy: boolean; onSave: (r: 'Pass' | 'Fail', note: string) => void; line: ReactNode }) {
  const [note, setNote] = useState('');
  return (
    <>
      {line}
      <div className="box" style={{ borderColor: 'var(--bad)' }}>
        <div className="qcbig">
          <div className="tick bad">!</div>
          <div>
            <b>{t}</b>
            <br />
            <span className="meta">Only items with a valid QC can go on a shelf. Do the QC check now, or put the item in R1.</span>
          </div>
        </div>
        <label className="field" style={{ marginTop: 12 }}>
          QC note (what you checked, or what is wrong)
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Visual check and function test OK" />
        </label>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn primary" disabled={busy} onClick={() => onSave('Pass', note)}>
            ✓ QC done and passed
          </button>
          <button className="btn" disabled={busy} onClick={() => onSave('Fail', note)}>
            QC failed, put it in R1
          </button>
        </div>
      </div>
    </>
  );
}

function ScanBox({ fn, ph, onCam }: { fn: (v: string) => void; ph: string; onCam: (fn: (c: string) => void) => void }) {
  const { d } = useApp();
  const [v, setV] = useState('');
  return (
    <>
      <div className="scanbox">
        <input autoFocus placeholder={ph} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fn(v)} autoComplete="off" autoCapitalize="characters" />
        <button className="btn primary" onClick={() => fn(v)}>
          Next
        </button>
      </div>
      <button className="btn primary" onClick={() => onCam(fn)}>
        Scan QR with phone camera
      </button>
      <p className="meta" style={{ margin: '10px 0 6px' }}>
        Or tap the storage area:
      </p>
      <div className="chips">
        {d.b.shelves.map((s) => (
          <button key={s.code} className="chip" onClick={() => fn(s.code)}>
            {s.code} {s.name}
          </button>
        ))}
      </div>
    </>
  );
}
